import { formatTimeWithTzShort } from '@/utils'

export type PublishHumanLog = {
  tsRaw?: string
  timestamp: string
  step: string
  source: string
  message: string
  color: string
  icon?: string
  metrics?: Array<{ label: string; value: string | number; color?: string }>
}

/**
 * Turns raw publish log entries into a compact, deduped timeline format.
 * This is intentionally best-effort and tolerant of missing fields.
 */
export function parsePublishLogsToHumanReadable(entriesRaw: unknown): { logs: PublishHumanLog[] } {
  const entries = Array.isArray(entriesRaw) ? entriesRaw : []

  const logs = (entries as any[]).map((entry: any): PublishHumanLog | null => {
    const level = entry?.level || 'info'
    const msg = entry?.message || entry?.msg || ''
    const rawStep = entry?.step || 'unknown'
    const ts = entry?.ts ? new Date(entry.ts) : null

    // Remove noisy debug-only entry from the friendly timeline
    if (
      rawStep === 'inventory_controller' &&
      (entry?.msg === 'debug_vin_selected' || entry?.message === 'debug_vin_selected')
    ) {
      return null
    }

    const timestamp = ts ? formatTimeWithTzShort(ts) : ''

    // Determine color and icon based on level and message
    let color = 'primary'
    let icon: string | undefined = undefined
    if (level === 'error') {
      color = 'error'
      icon = 'mdi-alert-circle'
    } else if (level === 'warn') {
      color = 'warning'
      icon = 'mdi-alert'
    }

    const message = String(msg || '').trim()
    const metrics: Array<{ label: string; value: string | number; color?: string }> = []

    // generic UX: treat explicit "start"/"summary" codes as start/end markers when message is still a code
    if (entry?.msg === 'start') {
      color = 'info'
      icon = 'mdi-play-circle'
    } else if (entry?.msg === 'summary' || String(entry?.msg || '').includes('finished')) {
      color = 'success'
      icon = 'mdi-check-circle'
    }

    const service = String(entry?.service || '').trim()
    const source = service
      ? service.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
      : String(rawStep).replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())

    return {
      tsRaw: typeof entry?.ts === 'string' ? entry.ts : undefined,
      timestamp,
      step: String(rawStep).replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
      source,
      message: message || 'unknown',
      color,
      icon,
      metrics: metrics.length ? metrics : undefined,
    }
  })

  const filtered = logs.filter((log): log is PublishHumanLog => !!log && !!log.message && log.message !== 'unknown')

  // Dedupe identical lines (common when merged logs contain the same summary twice)
  const deduped: PublishHumanLog[] = []
  const seen = new Set<string>()
  for (const l of filtered) {
    const key = `${l.timestamp}|${l.source}|${l.message}|${JSON.stringify(l.metrics || [])}`
    if (seen.has(key)) continue
    seen.add(key)
    deduped.push(l)
  }

  return { logs: deduped }
}

